/**
 * 3D arrow primitive for the drill canvas. Renders a curved tube
 * (Catmull-Rom) with a cone tip, coloured + styled by the source
 * DrillArrow's `kind` / `style` / `color` / `curvature`.
 *
 * Solid: continuous tube. Dashed: discrete tube segments along the curve.
 * Dotted: small spheres along the curve.
 */
import { forwardRef, useMemo } from 'react';
import * as THREE from 'three';
import type { DrillArrow } from '@4cm/core/drills/types';
import { toolForMode, DRAWING_TOOLS } from '../drawingTypes';

interface Props {
  arrow: DrillArrow;
  fromWorld: { x: number; z: number };
  toWorld: { x: number; z: number };
}

/** Arrow renders just above the pitch so it's visible from above. */
const ARROW_Y = 0.35;
/** Tube radius (metres). Tuned to be visible but not dominate. */
const TUBE_RADIUS = 0.18;
const HEAD_RADIUS = 0.55;
const HEAD_LENGTH = 0.9;

export const DrillArrow3D = forwardRef<THREE.Group, Props>(function DrillArrow3D(
  { arrow, fromWorld, toWorld },
  ref,
) {
  const kind = arrow.kind ?? 'pass';
  const tool = toolForMode(kind) ?? DRAWING_TOOLS[0];
  const color = arrow.color ?? tool.color;
  const style = arrow.style ?? tool.style;
  const curvature = arrow.curvature ?? tool.curvature;

  // Build the curve. Quadratic Bézier via a midpoint offset
  // perpendicular to the chord.
  const curve = useMemo(() => {
    const a = new THREE.Vector3(fromWorld.x, ARROW_Y, fromWorld.z);
    const b = new THREE.Vector3(toWorld.x, ARROW_Y, toWorld.z);
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.05) return null;
    // Perpendicular offset for curvature (in xz plane).
    const perpX = -dz / len;
    const perpZ = dx / len;
    const off = curvature * len * 0.4;
    const mid = new THREE.Vector3((a.x + b.x) / 2 + perpX * off, ARROW_Y, (a.z + b.z) / 2 + perpZ * off);
    return new THREE.QuadraticBezierCurve3(a, mid, b);
  }, [fromWorld.x, fromWorld.z, toWorld.x, toWorld.z, curvature]);

  // Tangent at the endpoint for the cone tip orientation.
  const headTransform = useMemo(() => {
    if (!curve) return null;
    const end = curve.getPoint(1);
    const tangent = curve.getTangent(1).normalize();
    // Pull the cone back so its base is on the curve end, tip points outward.
    const base = end.clone().sub(tangent.clone().multiplyScalar(HEAD_LENGTH / 2));
    // Default cone orientation is +Y; align to tangent.
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangent);
    return { position: base, quat };
  }, [curve]);

  if (!curve || !headTransform) return <group ref={ref} />;

  // Shorten the tube so its end doesn't poke through the cone.
  const tubeT = 1 - (HEAD_LENGTH / 2) / Math.max(0.1, curve.getLength());
  const tubeCurve = useMemo(() => {
    if (!curve) return null;
    const pts = [];
    const steps = 24;
    for (let i = 0; i <= steps; i += 1) {
      pts.push(curve.getPoint((i / steps) * tubeT));
    }
    return new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.2);
  }, [curve, tubeT]);

  if (!tubeCurve) return <group ref={ref} />;

  return (
    <group ref={ref} userData={{ kind: 'drillArrow', arrowKind: kind, color }}>
      {style === 'dotted' ? (
        <DottedArrowBody curve={tubeCurve} color={color} />
      ) : style === 'dashed' ? (
        <DashedArrowBody curve={tubeCurve} color={color} />
      ) : (
        <mesh>
          <tubeGeometry args={[tubeCurve, 32, TUBE_RADIUS, 8, false]} />
          <meshStandardMaterial color={color} roughness={0.5} />
        </mesh>
      )}
      {/* Arrowhead cone. */}
      <mesh position={headTransform.position} quaternion={headTransform.quat}>
        <coneGeometry args={[HEAD_RADIUS, HEAD_LENGTH, 16]} />
        <meshStandardMaterial color={color} roughness={0.5} />
      </mesh>
    </group>
  );
});

// ---- Style sub-renderers ---------------------------------------------------

function DashedArrowBody({ curve, color }: { curve: THREE.CatmullRomCurve3; color: string }): React.JSX.Element {
  // Render as a series of short tube segments with gaps.
  const segments = useMemo(() => {
    const total = curve.getLength();
    const dashLen = 0.6;
    const gapLen = 0.4;
    const period = dashLen + gapLen;
    const count = Math.max(1, Math.floor(total / period));
    const out: THREE.CatmullRomCurve3[] = [];
    for (let i = 0; i < count; i += 1) {
      const t0 = (i * period) / total;
      const t1 = Math.min(1, (i * period + dashLen) / total);
      const pts = [curve.getPoint(t0), curve.getPoint((t0 + t1) / 2), curve.getPoint(t1)];
      out.push(new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.1));
    }
    return out;
  }, [curve]);
  return (
    <group>
      {segments.map((seg, i) => (
        <mesh key={i}>
          <tubeGeometry args={[seg, 6, TUBE_RADIUS, 6, false]} />
          <meshStandardMaterial color={color} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

function DottedArrowBody({ curve, color }: { curve: THREE.CatmullRomCurve3; color: string }): React.JSX.Element {
  // Render as small spheres along the curve.
  const dots = useMemo(() => {
    const total = curve.getLength();
    const spacing = 0.45;
    const count = Math.max(1, Math.floor(total / spacing));
    const out: THREE.Vector3[] = [];
    for (let i = 0; i < count; i += 1) {
      out.push(curve.getPoint(i / Math.max(1, count - 1)));
    }
    return out;
  }, [curve]);
  return (
    <group>
      {dots.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[TUBE_RADIUS * 1.1, 8, 6]} />
          <meshStandardMaterial color={color} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
}
